import { logger } from '@pkg/logger';
import { ref, onBeforeUnmount, type Ref } from 'vue';
import { useHead } from '#imports';

declare global {
  interface Window {
    onloadTurnstileCallback?: () => void;
    turnstile: any;
  }
}

const SITE_KEY_REGEX = /^[A-Za-z0-9_-]{10,}$/;
const CONTAINER_ID_REGEX = /^[A-Za-z][A-Za-z0-9_-]*$/;

function normalizeContainer(
  turnstileContainer: Ref<HTMLElement | null> | string,
): HTMLElement | null {
  if (typeof turnstileContainer !== 'string') {
    return turnstileContainer.value;
  }
  if (!CONTAINER_ID_REGEX.test(turnstileContainer)) {
    logger.warn('[Turnstile] Invalid container id provided, refusing to render.');
    return null;
  }
  return document.getElementById(turnstileContainer);
}

export function useTurnstile(siteKey: string, turnstileContainer: Ref<HTMLElement | null> | string = 'cf-turnstile') {
  const status = ref<'loading' | 'ready' | 'error' | 'verified'>('loading');
  const error = ref<string | null>(null);
  const token = ref<string | null>(null);
  let widgetId: string | undefined = undefined;
  
  const _renderWidget = () => {
    if (!window?.turnstile) {
      status.value = 'error';
      error.value = 'Turnstile is not available on window.';
      logger.error('[Turnstile] Cannot render widget: window.turnstile is not defined.');
      return;
    }

    if (!SITE_KEY_REGEX.test(siteKey)) {
      status.value = 'error';
      error.value = 'Invalid Turnstile site key.';
      logger.error('[Turnstile] Invalid site key, refusing to render.');
      return;
    }

    const targetElement = normalizeContainer(turnstileContainer);

    if (!targetElement) {
      status.value = 'error';
      error.value = 'Target element not found.';
      return;
    }

    
    try {
      status.value = 'loading';
      error.value = null;
      token.value = null;

      widgetId = window.turnstile.render(targetElement, {
        sitekey: siteKey,
        theme: 'light',
        callback: (receivedToken: string) => {
          logger.debug('[Turnstile] Token received');
          token.value = receivedToken;
          status.value = 'verified';
        },
        'error-callback': () => {
          logger.error('[Turnstile] Challenge failed.');
          token.value = null;
          status.value = 'error';
          error.value = 'Challenge failed.';
        },
        'expired-callback': () => {
          logger.warn('[Turnstile] Token expired.');
          token.value = null;
          try {
            window.turnstile.reset(widgetId);
          } catch (e) {
            logger.debug('[Turnstile] Could not reset widget, falling back to restart', e);
            restart();
          }
        }
      });
      status.value = 'ready';
    } catch (e: any) {
      status.value = 'error';
      error.value = `Failed to render widget: ${e.message}`;
      logger.error('[Turnstile] Render error:', e);
    }
  };

  const restart = () => {
    try {
      if (window.turnstile && widgetId) {
        window.turnstile.remove(widgetId);
        widgetId = undefined;
      }
    } catch (e) {
      logger.debug('[Turnstile] Could not remove widget on restart - it may no longer exist', e);
    }
    _renderWidget();
  };

  const loadScript = () => {
    if (window.turnstile) {
      logger.debug('[Turnstile] Script already loaded. Rendering widget directly.');
      _renderWidget();
      return;
    }

    if (window.onloadTurnstileCallback) {
      const originalCallback = window.onloadTurnstileCallback;
      window.onloadTurnstileCallback = () => {
        originalCallback();
        _renderWidget();
      };
      return;
    } else {
      window.onloadTurnstileCallback = () => {
        _renderWidget();
      };
    }

    useHead({
      script: [{
        src: 'https://challenges.cloudflare.com/turnstile/v0/api.js?onload=onloadTurnstileCallback',
        async: true,
        defer: true,
      }],
    });
  };

  const render = () => {
    if (window.turnstile) {
      _renderWidget();
    } else {
      loadScript();
    }
  };

  onBeforeUnmount(() => {
    try {
      if (window.turnstile && widgetId) {
        window.turnstile.remove(widgetId);
      }
    } catch (e) {
      logger.debug('[Turnstile] Could not remove widget on unmount - it may no longer exist', e);
    }
    window.onloadTurnstileCallback = undefined;
  });

  return { status, error, token, render, restart };
}